// E-Sign Via Derwent — Full-page document detail view.
//
// Replaces the old right-hand drawer with a dedicated route
// (`/esign/:docId`). Shows every request detail across the full webpage:
// customer name, signer list with per-status chips, documents, signed vs
// outstanding split, activity/audit history, and per-signer copy-message
// buttons (Email / Text / Signing Link) plus a Resend action.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ChevronLeft, Copy, Download, FileText, History, Loader2, Mail,
  MessageSquare, Link2, RotateCw, Send, Settings, Shield, Trash2, Users, X,
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import EsignFieldEditor from './EsignFieldEditor';
import { CorrectSignerDialog } from '../components/esign/CorrectSignerDialog';
import EsignSmsHistory from '../components/esign/EsignSmsHistory';

const API = process.env.REACT_APP_BACKEND_URL;
const BRAND = '#0057FF';

// Per-signer status → chip visuals and canonical labels.
const SIGNER_META = {
  pending:  { label: 'Not sent',   cls: 'bg-slate-100 text-slate-600' },
  sent:     { label: 'Sent',       cls: 'bg-blue-50 text-blue-700' },
  viewed:   { label: 'Viewed',     cls: 'bg-amber-50 text-amber-700' },
  signed:   { label: 'Signed',     cls: 'bg-emerald-50 text-emerald-700' },
  declined: { label: 'Declined',   cls: 'bg-rose-50 text-rose-700' },
  expired:  { label: 'Expired',    cls: 'bg-rose-50 text-rose-700' },
};

// Overall document status → chip visuals.
const STATUS_META = {
  draft:            { label: 'Draft',            cls: 'bg-slate-100 text-slate-600',    dot: 'bg-slate-400' },
  sent:             { label: 'Sent',             cls: 'bg-blue-50 text-blue-700',       dot: 'bg-blue-500' },
  viewed:           { label: 'Viewed',           cls: 'bg-amber-50 text-amber-700',     dot: 'bg-amber-500' },
  partially_signed: { label: 'Partially signed', cls: 'bg-amber-50 text-amber-700',     dot: 'bg-amber-500' },
  completed:        { label: 'Completed',        cls: 'bg-emerald-50 text-emerald-700', dot: 'bg-emerald-500' },
  voided:           { label: 'Voided',           cls: 'bg-rose-50 text-rose-700',       dot: 'bg-rose-500' },
  expired:          { label: 'Expired',          cls: 'bg-rose-50 text-rose-700',       dot: 'bg-rose-500' },
};

const fmtDate = (iso) => iso ? new Date(iso).toLocaleString('en-AU', {
  day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
}) : '—';

// Per-signer draft message templates. Broker can copy and paste to
// email or text. Each includes the signer's first name and unique link.
const emailMessage = (firstName, link) => (
`Hi ${firstName},

Your documents are ready to be reviewed and signed.

Please use the secure link below to complete your forms:
${link}

Please let me know if you have any questions or experience any issues accessing the documents.

Kind regards,`);

const smsMessage = (firstName, link) => (
`Hi ${firstName}, your documents are ready to sign. Please use this secure link to complete them: ${link} — let me know if you have any issues.`);

// Group draft message templates. Used when the broker wants to send ONE
// combined message that lists every signer's individual signing link
// (e.g. couples on the same email, family group threads). Each signer
// still gets their own unique secure link inside the message body.
const groupEmailMessage = (signers) => {
  const lines = signers.map((s) => {
    const first = firstNameOf(s.name);
    const link = `${window.location.origin}/esign/sign/${s.token}`;
    return `• ${first} (${s.name}): ${link}`;
  }).join('\n');
  return (
`Hi everyone,

Your documents are ready to be reviewed and signed. Each of you has your own secure link below — please use the one with your name on it:

${lines}

Please let me know if you have any questions or experience any issues accessing the documents.

Kind regards,`);
};

const groupSmsMessage = (signers) => {
  const lines = signers.map((s) => {
    const first = firstNameOf(s.name);
    const link = `${window.location.origin}/esign/sign/${s.token}`;
    return `${first}: ${link}`;
  }).join('\n');
  return (
`Hi all, your documents are ready to sign. Each person has their own secure link — please use yours below:
${lines}
Let me know if any issues.`);
};

const firstNameOf = (name) => (name || '').trim().split(/\s+/)[0] || 'there';

const doCopy = async (text, label) => {
  try {
    await navigator.clipboard.writeText(text);
    toast.success(`${label} copied to clipboard`);
  } catch {
    toast.error('Copy failed — please copy manually');
  }
};

export default function EsignDocumentDetail() {
  const { docId } = useParams();
  const navigate = useNavigate();
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [showEditor, setShowEditor] = useState(false);
  const [linkOpen, setLinkOpen] = useState(false);
  const [correcting, setCorrecting] = useState(null);

  const authHeaders = useMemo(() => ({
    Authorization: `Bearer ${localStorage.getItem('auth_token')}`,
  }), []);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/esign/documents/${docId}`, { headers: authHeaders });
      setDoc(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load document');
    } finally { setLoading(false); }
  }, [docId, authHeaders]);
  useEffect(() => { load(); }, [load]);

  const send = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/api/esign/documents/${docId}/send`, { base_url: window.location.origin }, { headers: authHeaders });
      toast.success('Document sent — signing links generated below');
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Send failed'); }
    finally { setBusy(false); }
  };

  const voidDoc = async () => {
    if (!window.confirm('Void this document? Signers won\'t be able to sign after this.')) return;
    try {
      await axios.post(`${API}/api/esign/documents/${docId}/void`, {}, { headers: authHeaders });
      toast.success('Voided');
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Void failed'); }
  };

  const resendSigner = async (s) => {
    try {
      const { data } = await axios.post(`${API}/api/esign/documents/${docId}/signers/${s.id}/resend`, {}, { headers: authHeaders });
      if (data.dry_run) toast.message('SMS is not configured — copy the text message below and send it manually');
      else if (data.delivered) toast.success(`Reminder SMS sent to ${firstNameOf(s.name)} (${data.mobile}) with their existing signing link`);
      else toast.error(`ClickSend did not confirm delivery to ${data.mobile} — try again shortly`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Reminder failed'); }
  };

  const correctSigner = (s) => setCorrecting(s);

  const saveAsTemplate = async () => {
    const name = window.prompt('Template name', doc?.title); if (!name) return;
    const fd = new FormData(); fd.append('name', name); fd.append('from_document_id', docId); fd.append('description', window.prompt('When should the team use it? (optional)') || '');
    try { await axios.post(`${API}/api/esign/templates`, fd, { headers: authHeaders }); toast.success('Saved to E-Sign Templates — PDF, signer roles and fields included'); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Could not save template'); }
  };

  const linkToClient = async (application_id, label) => {
    try {
      await axios.patch(`${API}/api/esign/documents/${docId}/link`, { application_id }, { headers: authHeaders });
      toast.success(application_id ? `Linked to ${label}` : 'Unlinked from client record');
      setLinkOpen(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Link failed'); }
  };

  const download = (path, filename) => {
    axios.get(`${API}/api/esign/documents/${docId}/${path}`, {
      headers: authHeaders, responseType: 'blob',
    }).then((r) => {
      const blob = new Blob([r.data], { type: 'application/pdf' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = filename;
      link.click();
    }).catch((e) => toast.error(e?.response?.data?.detail || 'Download failed'));
  };

  if (loading) {
    return (
      <div className="cp-admin min-h-screen">
        <Header />
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 flex items-center justify-center h-64">
          <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
        </main>
      </div>
    );
  }

  if (!doc) return null;

  const meta = STATUS_META[doc.is_expired ? 'expired' : doc.status] || STATUS_META.draft;
  const signers = doc.signers || [];
  const signedCount = signers.filter((s) => s.status === 'signed').length;
  const outstanding = signers.filter((s) => s.status !== 'signed' && s.status !== 'declined');
  const fieldCount = doc.fields?.length || 0;
  const customerName = doc.customer_name || signers[0]?.name || doc.title;
  const canCorrect = !['completed', 'voided'].includes(doc.status);

  return (
    <div className="cp-admin min-h-screen">
      <Header />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        {/* Breadcrumb + back */}
        <button type="button" onClick={() => navigate('/onboarding?pipeline=esign')} data-testid="esign-detail-back"
                className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-500 hover:text-slate-900 transition-colors mb-4">
          <ChevronLeft className="w-4 h-4" /> Client Portal · E-Sign
        </button>

        {/* Hero */}
        <div className="rounded-3xl bg-white border border-slate-200/80 shadow-sm overflow-hidden mb-5">
          <div className="p-6 sm:p-8 flex flex-col md:flex-row md:items-start md:justify-between gap-4">
            <div className="min-w-0 flex-1">
              <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-1">
                {doc.reference_number}
              </p>
              <h1 className="text-2xl sm:text-4xl font-black tracking-tight text-slate-950 break-words"
                  data-testid="esign-detail-customer">
                {customerName}
              </h1>
              <p className="text-sm sm:text-base font-semibold text-slate-500 mt-1 break-words" data-testid="esign-detail-title">
                <FileText className="w-4 h-4 inline-block -mt-0.5 mr-1 text-slate-400" />
                {doc.title}
              </p>
              <div className="mt-3 flex flex-wrap items-center gap-3 text-xs text-slate-500">
                <span className={`inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full ${meta.cls}`}>
                  <span className={`w-1.5 h-1.5 rounded-full ${meta.dot}`} /> {meta.label}
                </span>
                <span>· {signers.length} signer{signers.length === 1 ? '' : 's'} · {signedCount}/{signers.length} signed</span>
                <span>· Created by <strong className="text-slate-700">{doc.broker_name}</strong></span>
                {doc.template && <span>· From template <strong className="text-slate-700">{doc.template.name} v{doc.template.version}</strong></span>}
                {doc.application_id
                  ? <span data-testid="esign-detail-linked">· Linked to <button type="button" onClick={() => navigate(`/onboarding/${doc.application_id}`)} className="font-bold text-violet-700 hover:underline">client portal record</button> <button type="button" onClick={() => linkToClient('', '')} className="text-slate-400 hover:text-rose-600 underline" data-testid="esign-detail-unlink">unlink</button></span>
                  : <span>· Standalone <button type="button" onClick={() => setLinkOpen(true)} data-testid="esign-detail-link" className="font-bold text-violet-700 hover:underline">link to a client record</button></span>}
              </div>
              {linkOpen && <LinkPicker authHeaders={authHeaders} onPick={linkToClient} onClose={() => setLinkOpen(false)} />}
            </div>

            {/* Action buttons */}
            <div className="grid grid-cols-2 sm:flex sm:flex-wrap gap-2 [&>button]:min-h-[42px] sm:[&>button]:min-h-0 [&>button]:justify-center">
              <button type="button" onClick={saveAsTemplate} data-testid="esign-detail-save-template"
                      className="inline-flex items-center gap-1.5 rounded-full text-xs font-bold px-4 py-2 bg-white text-slate-700 border border-slate-300 hover:bg-slate-100">
                <Copy className="w-3.5 h-3.5" /> Save as template
              </button>
              {doc.status === 'draft' && (
                <>
                  <button type="button" onClick={() => setShowEditor(true)} data-testid="esign-detail-prepare"
                          className="inline-flex items-center gap-1.5 rounded-full text-xs font-bold px-4 py-2 text-white shadow-sm hover:opacity-90"
                          style={{ backgroundColor: BRAND }}>
                    <Settings className="w-3.5 h-3.5" />
                    {fieldCount === 0 ? 'Prepare fields' : 'Edit fields'}
                  </button>
                  <button type="button" onClick={send} disabled={busy || fieldCount === 0} data-testid="esign-detail-send"
                          className="inline-flex items-center gap-1.5 rounded-full text-xs font-bold px-4 py-2 bg-[#15133A] text-white shadow-sm hover:opacity-90 disabled:opacity-50"
                          title={fieldCount === 0 ? 'Place at least one field before sending' : ''}>
                    {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                    Send for signing
                  </button>
                </>
              )}
              <button type="button" onClick={() => download('file', `${doc.title}.pdf`)} data-testid="esign-detail-download-original"
                      className="inline-flex items-center gap-1.5 rounded-full text-xs font-bold px-4 py-2 bg-slate-100 text-slate-700 border border-slate-200 hover:bg-slate-200">
                <FileText className="w-3.5 h-3.5" /> Original PDF
              </button>
              {doc.status === 'completed' && (
                <>
                  <button type="button" onClick={() => download('final', `${doc.title}-signed-with-certificate.pdf`)} data-testid="esign-detail-download-final"
                          className="inline-flex items-center gap-1.5 rounded-full text-xs font-bold px-4 py-2 text-white shadow-sm hover:opacity-90"
                          style={{ backgroundColor: BRAND }}>
                    <Download className="w-3.5 h-3.5" /> Signed + certificate
                  </button>
                  <button type="button" onClick={() => download('signed-pdf', `${doc.title}-signed.pdf`)}
                          className="inline-flex items-center gap-1.5 rounded-full text-xs font-bold px-4 py-2 bg-slate-100 text-slate-700 border border-slate-200 hover:bg-slate-200">
                    <FileText className="w-3.5 h-3.5" /> Signed only
                  </button>
                  <button type="button" onClick={() => download('certificate', `certificate-${doc.reference_number}.pdf`)}
                          className="inline-flex items-center gap-1.5 rounded-full text-xs font-bold px-4 py-2 bg-slate-100 text-slate-700 border border-slate-200 hover:bg-slate-200">
                    <Shield className="w-3.5 h-3.5" /> Certificate
                  </button>
                </>
              )}
              {doc.status !== 'completed' && doc.status !== 'voided' && (
                <button type="button" onClick={voidDoc}
                        className="inline-flex items-center gap-1.5 rounded-full text-xs font-bold px-4 py-2 text-rose-600 border border-rose-200 hover:bg-rose-50">
                  <Trash2 className="w-3.5 h-3.5" /> Void
                </button>
              )}
            </div>
          </div>

          {/* Timeline strip */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-slate-100 border-t border-slate-200/80">
            <TimelineCell label="Created" value={fmtDate(doc.created_at)} />
            <TimelineCell label="Sent" value={fmtDate(doc.sent_at)} />
            <TimelineCell label="Completed" value={fmtDate(doc.completed_at)} />
            <TimelineCell label="Last activity" value={fmtDate(doc.last_activity_at)} />
          </div>
        </div>

        {/* Two-column body: Signers (left) · Outstanding + Audit (right) */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          <section className="lg:col-span-2 space-y-4">
            {/* Signers with draft messages */}
            <div className="rounded-3xl bg-white border border-slate-200/80 shadow-sm overflow-hidden">
              <div className="px-6 py-4 border-b border-slate-100">
                <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Signers &amp; draft messages</p>
                <p className="text-lg font-black text-slate-900 mt-0.5">
                  {signedCount === signers.length && signers.length > 0 ? 'Everyone has signed' : `${signedCount} of ${signers.length} signed`}
                </p>
              </div>
              <div className="divide-y divide-slate-100">
                {/* Group message — visible when 2+ signers and doc has been sent */}
                {doc.status !== 'draft' && signers.length >= 2 && (
                  <div className="p-5 bg-gradient-to-br from-slate-50 to-white" data-testid="esign-group-message-block">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-full flex items-center justify-center shrink-0 bg-slate-900">
                        <Users className="w-5 h-5 text-white" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-baseline gap-2 flex-wrap">
                          <p className="text-base font-bold text-slate-900">Send to everyone at once</p>
                          <span className="inline-flex items-center text-[9.5px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full bg-[#15133A] text-white">
                            All {signers.length} signers
                          </span>
                        </div>
                        <p className="text-xs text-slate-500 mt-0.5">One message containing every signer&apos;s individual secure link — perfect for couples, family groups, or shared inboxes.</p>
                      </div>
                    </div>

                    <div className="mt-4 rounded-2xl bg-white border border-slate-200 p-4">
                      <div className="flex items-center justify-between gap-2 mb-2">
                        <p className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Group draft message</p>
                      </div>
                      <pre className="text-xs text-slate-700 whitespace-pre-wrap [overflow-wrap:anywhere] font-sans leading-relaxed">
{groupEmailMessage(signers)}
                      </pre>
                      <div className="mt-3 flex flex-wrap gap-2">
                        <button type="button" data-testid="esign-copy-group-email"
                                onClick={() => doCopy(groupEmailMessage(signers), 'Group email message')}
                                className="inline-flex items-center gap-1.5 rounded-full text-[11px] font-bold px-3 py-1.5 text-white shadow-sm hover:opacity-90"
                                style={{ backgroundColor: BRAND }}>
                          <Mail className="w-3 h-3" /> Copy Group Email
                        </button>
                        <button type="button" data-testid="esign-copy-group-sms"
                                onClick={() => doCopy(groupSmsMessage(signers), 'Group text message')}
                                className="inline-flex items-center gap-1.5 rounded-full text-[11px] font-bold px-3 py-1.5 bg-[#15133A] text-white shadow-sm hover:opacity-90">
                          <MessageSquare className="w-3 h-3" /> Copy Group Text
                        </button>
                        <button type="button" data-testid="esign-copy-all-links"
                                onClick={() => doCopy(signers.map((s) => `${firstNameOf(s.name)}: ${window.location.origin}/esign/sign/${s.token}`).join('\n'), 'All signing links')}
                                className="inline-flex items-center gap-1.5 rounded-full text-[11px] font-bold px-3 py-1.5 bg-white text-slate-700 border border-slate-300 hover:bg-slate-100">
                          <Link2 className="w-3 h-3" /> Copy All Links
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {signers.map((s) => {
                  const smeta = SIGNER_META[s.status] || SIGNER_META.pending;
                  const firstName = firstNameOf(s.name);
                  const link = `${window.location.origin}/esign/sign/${s.token}`;
                  return (
                    <div key={s.id} className="p-4 sm:p-5" data-testid={`esign-signer-block-${s.id}`}>
                      <div className="flex flex-wrap items-start gap-3">
                        <div className="w-10 h-10 rounded-full flex items-center justify-center font-bold text-white text-sm shrink-0"
                             style={{ backgroundColor: BRAND }}>
                          {s.name?.[0]?.toUpperCase() || '?'}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-baseline gap-2 flex-wrap">
                            <p className="text-base font-bold text-slate-900 break-words">{s.name}</p>
                            <span className={`inline-flex items-center text-[9.5px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full ${smeta.cls}`}>
                              {smeta.label}
                            </span>
                            {s.role && <span className="text-[10.5px] text-slate-400">· {s.role}</span>}
                          </div>
                          <p className="text-xs text-slate-500 break-all mt-0.5">{s.email}{s.mobile ? ` · ${s.mobile}` : ''}</p>
                          {s.signed_at && (
                            <p className="text-[11px] text-emerald-700 mt-0.5">
                              Signed {fmtDate(s.signed_at)}{s.signed_ip ? ` · IP ${s.signed_ip}` : ''}
                            </p>
                          )}
                          {s.status === 'viewed' && s.viewed_at && <p className="text-[11px] text-amber-700 mt-0.5">Opened {fmtDate(s.viewed_at)} — not yet signed</p>}
                          {s.status === 'declined' && <p className="text-[11px] text-rose-700 mt-0.5" data-testid={`esign-declined-${s.id}`}>Declined {fmtDate(s.declined_at)}{s.decline_reason ? ` — “${s.decline_reason}”` : ''}</p>}
                          {!!s.corrections?.length && <p className="text-[11px] text-slate-400 mt-0.5">Corrected {s.corrections.length}× · previously {s.corrections[s.corrections.length - 1].previous.email}{s.corrections[s.corrections.length - 1].previous.mobile ? ` · ${s.corrections[s.corrections.length - 1].previous.mobile}` : ''}</p>}
                        </div>
                        {canCorrect && s.status !== 'signed' && (
                          <button type="button" onClick={() => correctSigner(s)} data-testid={`esign-correct-${s.id}`}
                                  className="shrink-0 inline-flex items-center gap-1 rounded-full text-[11px] font-bold px-3 py-1.5 bg-white text-slate-700 border border-slate-300 hover:bg-slate-100">
                            <Settings className="w-3 h-3" /> Correct recipient
                          </button>
                        )}
                      </div>

                      {/* Draft email + quick-copy buttons */}
                      {doc.status !== 'draft' && (
                        <div className="mt-4 rounded-2xl bg-slate-50 border border-slate-200 p-4">
                          <div className="flex items-center justify-between gap-2 mb-2">
                            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Draft message for {firstName}</p>
                          </div>
                          <pre className="text-xs text-slate-700 whitespace-pre-wrap [overflow-wrap:anywhere] font-sans leading-relaxed">
{emailMessage(firstName, link)}
                          </pre>
                          <div className="mt-3 grid grid-cols-1 sm:flex sm:flex-wrap gap-2 [&>button]:min-h-[42px] sm:[&>button]:min-h-0 [&>button]:justify-center">
                            <button type="button" data-testid={`esign-copy-email-${s.id}`}
                                    onClick={() => doCopy(emailMessage(firstName, link), 'Email message')}
                                    className="inline-flex items-center gap-1.5 rounded-full text-[11px] font-bold px-3 py-1.5 text-white shadow-sm hover:opacity-90"
                                    style={{ backgroundColor: BRAND }}>
                              <Mail className="w-3 h-3" /> Copy Email Message
                            </button>
                            <button type="button" data-testid={`esign-copy-sms-${s.id}`}
                                    onClick={() => doCopy(smsMessage(firstName, link), 'Text message')}
                                    className="inline-flex items-center gap-1.5 rounded-full text-[11px] font-bold px-3 py-1.5 bg-[#15133A] text-white shadow-sm hover:opacity-90">
                              <MessageSquare className="w-3 h-3" /> Copy Text Message
                            </button>
                            <button type="button" data-testid={`esign-copy-link-${s.id}`}
                                    onClick={() => doCopy(link, 'Signing link')}
                                    className="inline-flex items-center gap-1.5 rounded-full text-[11px] font-bold px-3 py-1.5 bg-white text-slate-700 border border-slate-300 hover:bg-slate-100">
                              <Link2 className="w-3 h-3" /> Copy Signing Link
                            </button>
                            {s.status !== 'signed' && !['completed', 'voided'].includes(doc.status) && (
                              <button type="button" data-testid={`esign-resend-${s.id}`}
                                      onClick={() => resendSigner(s)} disabled={!s.mobile}
                                      title={s.mobile ? `Resend the original SMS with the same signing link to ${s.mobile}` : 'No mobile number saved for this signer'}
                                      className="inline-flex items-center gap-1.5 rounded-full text-[11px] sm:text-[11px] font-bold px-3 py-2.5 sm:py-1.5 min-h-[42px] sm:min-h-0 bg-violet-50 text-violet-800 border border-violet-200 hover:bg-violet-100 disabled:opacity-40">
                                <RotateCw className="w-3 h-3" /> Send reminder{s.reminder_count ? ` (${s.reminder_count})` : ''}
                              </button>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Document meta */}
            <div className="rounded-3xl bg-white border border-slate-200/80 shadow-sm p-6">
              <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Document details</p>
              <dl className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                <MetaRow label="Document name" value={doc.title} />
                <MetaRow label="Filename" value={doc.pdf_filename || '—'} />
                <MetaRow label="Reference" value={doc.reference_number} mono />
                <MetaRow label="Fields placed" value={fieldCount} />
                <MetaRow label="SHA-256" value={doc.pdf_hash} mono wrap />
                <MetaRow label="Broker" value={doc.broker_name} />
              </dl>
            </div>
          </section>

          {/* Right column — outstanding + audit trail */}
          <aside className="space-y-4">
            <div className="rounded-3xl bg-white border border-slate-200/80 shadow-sm p-5">
              <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Outstanding</p>
              <p className="text-2xl font-black text-slate-950 mt-0.5">{outstanding.length}</p>
              {outstanding.length === 0 ? (
                <p className="text-xs text-emerald-700 mt-1">All signers complete</p>
              ) : (
                <ul className="mt-3 space-y-2">
                  {outstanding.map((s) => (
                    <li key={s.id} className="flex items-center gap-2 text-xs">
                      <span className="w-2 h-2 rounded-full bg-amber-500" />
                      <span className="font-semibold text-slate-800 truncate">{s.name}</span>
                      <span className="text-slate-400 ml-auto">{SIGNER_META[s.status]?.label || s.status}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="rounded-3xl bg-white border border-slate-200/80 shadow-sm p-5">
              <div className="flex items-center gap-2 mb-3">
                <History className="w-3.5 h-3.5 text-slate-400" />
                <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Activity &amp; audit</p>
              </div>
              <div className="max-h-[420px] overflow-y-auto space-y-2">
                {(doc.events || []).slice().reverse().map((ev) => (
                  <div key={ev.id} className="text-[11px]">
                    <p className="text-slate-400 font-mono">{fmtDate(ev.at)}</p>
                    <p className="text-slate-800 mt-0.5">
                      <strong className="text-slate-900">{ev.action.replace(/_/g, ' ')}</strong>
                      {ev.actor && <span className="text-slate-500"> · {ev.actor}</span>}
                    </p>
                    {ev.ip && <p className="text-slate-400 font-mono">{ev.ip}</p>}
                  </div>
                ))}
                {(!doc.events || doc.events.length === 0) && (
                  <p className="text-xs text-slate-400">No events yet.</p>
                )}
              </div>
            </div>

            <EsignSmsHistory docId={docId} headers={authHeaders} refreshKey={doc.last_activity_at} />
          </aside>
        </div>
      </main>
      <Footer />

      {correcting && <CorrectSignerDialog docId={docId} signer={correcting} headers={authHeaders} docStatus={doc?.status} onClose={() => setCorrecting(null)} onDone={() => load()} />}
      {showEditor && (
        <div className="fixed inset-0 z-50 bg-slate-950/60">
          <div className="absolute inset-0 overflow-y-auto">
            <div className="min-h-full flex items-center justify-center p-4">
              <div className="w-full max-w-6xl rounded-2xl bg-white shadow-2xl">
                <div className="p-3 flex items-center justify-between border-b border-slate-100">
                  <p className="text-sm font-bold text-slate-900">Prepare fields</p>
                  <button type="button" onClick={() => setShowEditor(false)} className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-slate-100">
                    <X className="w-4 h-4 text-slate-500" />
                  </button>
                </div>
                <EsignFieldEditor
                  doc={doc}
                  authHeaders={authHeaders}
                  onDone={() => { setShowEditor(false); load(); }}
                  onClose={() => setShowEditor(false)}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const TimelineCell = ({ label, value }) => (
  <div className="bg-white px-5 py-3">
    <p className="text-[9.5px] font-bold uppercase tracking-widest text-slate-400">{label}</p>
    <p className="text-sm font-bold text-slate-900 mt-0.5">{value}</p>
  </div>
);

const MetaRow = ({ label, value, mono, wrap }) => (
  <div>
    <dt className="text-[10px] font-bold uppercase tracking-widest text-slate-400">{label}</dt>
    <dd className={`text-sm text-slate-800 mt-0.5 ${mono ? 'font-mono' : 'font-semibold'} ${wrap ? 'break-all text-[11px]' : ''}`}>{value}</dd>
  </div>
);


// Pick a Client Portal record to attach a standalone envelope to.
const LinkPicker = ({ authHeaders, onPick, onClose }) => {
  const [apps, setApps] = useState(null);
  const [q, setQ] = useState('');
  useEffect(() => {
    axios.get(`${API}/api/onboarding/applications`, { headers: authHeaders })
      .then((r) => setApps((r.data.items || []).map((a) => ({ ...a, applicant_names: (a.applicants || []).map((x) => `${x.first_name} ${x.last_name || ''}`.trim()).join(' & ') })))).catch(() => setApps([]));
  }, [authHeaders]);
  const rows = (apps || []).filter((a) => !q || `${a.applicant_names || ''} ${a.reference || ''}`.toLowerCase().includes(q.toLowerCase())).slice(0, 8);
  return (
    <div className="mt-3 rounded-2xl border border-violet-200 bg-violet-50/60 p-3" data-testid="esign-link-picker">
      <div className="flex items-center gap-2">
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search client portals by name or reference…" data-testid="esign-link-search"
               className="flex-1 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-slate-900" />
        <button type="button" onClick={onClose} className="text-xs font-bold text-slate-500">Cancel</button>
      </div>
      <div className="mt-2 space-y-1 max-h-56 overflow-auto">
        {apps === null && <Loader2 className="w-4 h-4 animate-spin text-slate-400" />}
        {apps !== null && !rows.length && <p className="text-xs text-slate-500">No client portals match.</p>}
        {rows.map((a) => (
          <button key={a.id} type="button" onClick={() => onPick(a.id, a.applicant_names || a.reference)} data-testid={`esign-link-pick-${a.id}`}
                  className="w-full flex items-center justify-between rounded-xl bg-white border border-slate-200 px-3 py-2 text-left hover:border-slate-900">
            <span className="text-sm font-bold text-slate-900 truncate">{a.applicant_names || 'Client'}</span>
            <span className="text-[10px] text-slate-500 shrink-0 ml-2">{a.reference} · {a.status}</span>
          </button>
        ))}
      </div>
    </div>
  );
};
